import {
  ApiError,
  ApiErrorInitialState,
} from '@hellodarwin/core/lib/features/entities/api-entitites';
import type { AssetEntity } from '@hellodarwin/core/lib/features/entities/assets-entities';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../../app/app-store';
import ClientApiV2 from '../client-api-v2';
import SliceRequest from '../slice-request';

type ApplicationDocumentsStatus =
  | 'idle'
  | 'pending'
  | 'succeeded'
  | 'failed';

type ApplicationDocumentsState = {
  itemsByApplication: Record<string, AssetEntity[]>;
  statusByApplication: Record<string, ApplicationDocumentsStatus>;
  error: ApiError;
};

const initialState: ApplicationDocumentsState = {
  itemsByApplication: {},
  statusByApplication: {},
  error: ApiErrorInitialState,
};

const emptyApplicationDocuments: AssetEntity[] = [];

export const fetchApplicationDocuments = SliceRequest<
  AssetEntity[],
  { api: ClientApiV2; applicationId: string }
>('fetchApplicationDocuments', async ({ api, applicationId }) => {
  const response = await api.get<AssetEntity[]>(
    `/ginStep/application/assets/${applicationId}`,
  );
  return response.data;
});

const applicationDocumentsSlice = createSlice({
  name: 'applicationDocuments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchApplicationDocuments.pending, (state, action) => {
      state.statusByApplication[action.meta.arg.applicationId] = 'pending';
    });
    builder.addCase(fetchApplicationDocuments.fulfilled, (state, action) => {
      const applicationId = action.meta.arg.applicationId;
      state.itemsByApplication[applicationId] = action.payload ?? [];
      state.statusByApplication[applicationId] = 'succeeded';
    });
    builder.addCase(fetchApplicationDocuments.rejected, (state, action) => {
      state.error = action.payload ?? ApiErrorInitialState;
      state.statusByApplication[action.meta.arg.applicationId] = 'failed';
    });
  },
});

export const selectApplicationDocuments = (
  state: RootState,
  applicationId: string | undefined,
) =>
  applicationId
    ? (state.applicationDocuments.itemsByApplication[applicationId] ??
      emptyApplicationDocuments)
    : emptyApplicationDocuments;

export const selectApplicationDocumentsStatus = (
  state: RootState,
  applicationId: string | undefined,
) =>
  applicationId
    ? (state.applicationDocuments.statusByApplication[applicationId] ?? 'idle')
    : 'idle';

export const applicationDocumentsReducer = applicationDocumentsSlice.reducer;
